import Typography from '@components/std-components/Typography'
import { CellItem } from '@components/UI-Components/HierarchyWiseItemsTable/components/Row/Cell'
import useOrderContext from '@modules/order/hooks/use-order-context'
import useViewContext from '@modules/order/hooks/use-view-context'
import { PRODUCT_OPERATIONS_LOADERS } from '@modules/order/stores/view/List/ListDataStore'
import { ListUIStore } from '@modules/order/stores/view/List/ListUIStore'
import formatNumberByLocale from '@utils/format-number-by-locale'
import classNames from 'classnames'
import isNumber from 'lodash/isNumber'
import { useObserver } from 'mobx-react-lite'
import React, { FC, memo, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import QtyCellLabelText from '../components/QtyCellLabelText'
import QtyCellValueText from '../components/QtyCellValueText'
import {
  getUnExcludedDoorIds,
  checkWhetherBookedOrNot,
  useProductStatus
} from '@components/UI-Components/ProductCard/ProductStatusHook'
import { nav } from '@stores/nav'
import { productPriceStore } from '@stores/productPricesStore'
import { VIPClientType } from '@modules/common/models/enums/VIPClientType'
import { getEnumValue } from '@utils/get-enum-value'
import filter from 'lodash/filter'
import { Checkbox, makeStyles } from '@material-ui/core'
import CheckBoxIcon from '@material-ui/icons/CheckBox'
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank'

const useStyle = makeStyles(theme => ({
  bookQty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    overflowY: 'auto',
    marginTop: '10px',
    height: 'max-content'
  },
  bookUnBookCheckbox: {
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column'
  },
  totalActualQty: {
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    paddingTop: '9px'
  },
  totalSizedQty: {
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column'
  },
  placeholderBlock: {
    height: '45.57px'
  },
  totalValueContainer: {
    paddingTop: '8px',
    paddingBottom: '5px'
  },
  sizesContainer: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    paddingBottom: '15px'
  },
  sizeContainer: {
    height: 32,
    display: 'flex',
    alignItems: 'center',
    paddingTop: 6
  },
  sizeToggleButton: {
    marginTop: '3px'
  },
  sizeLabel: {
    marginBottom: '2px'
  },
  bookQtyCheckbox: {
    color: theme.palette.text.primary
  },
  bookQtyUnCheckbox: {
    color: theme.palette.text.disabled
  }
}))

const BookQty: FC<CellItem> = ({ rowData }) =>
  useObserver(function useHook() {
    const { t } = useTranslation()
    const classes = useStyle()
    const viewContextVal = useViewContext()
    const {
      bookUnbookDoors,
      hideShowProductSizes,
      showProductSizes,
      getSizes,
      getBookedDoors,
      dataStore: { itemIndexObjectStore },
      isValueSelected
    } = viewContextVal.uiStore as ListUIStore
    const {
      uiStore: {
        dataStore: { summary },
        isOrderEditable
      }
    } = useOrderContext()
    const priceData = productPriceStore?.getPriceForProduct(rowData?.id)

    const { quantity, original } = rowData || {}
    const { sizes } = original || {}

    const bookedDoors = getBookedDoors(rowData?.id, summary?.doorOrders)
    const areAllDoorsBooked = useMemo(() => {
      // check for order-type door (1 object of doorOrder) else client order (array of object of door orders)
      return checkWhetherBookedOrNot(
        rowData?.excludedInDoors,
        filter(summary?.doorOrders, doorOrder => !doorOrder?.deleted),
        bookedDoors
      )
    }, [summary?.doorOrders, bookedDoors])

    const isRetail = useMemo(
      () => getEnumValue(VIPClientType.Internal) === nav.queryParams?.clientType,
      []
    )

    const bookedCells = useMemo(() => {
      const cellCurrency = isValueSelected
        ? priceData?.kpiCurrency
          ? priceData?.kpiCurrency
          : `${priceData?.[isRetail ? 'rtlCurrency' : 'whlCurrency']} `
        : ''
      return {
        cell1: {
          cellValue: quantity?.[`total${isValueSelected ? 'Value' : ''}ByDraftQuantity`],
          cellCurrency,
          cellLabel: isRetail
            ? isValueSelected
              ? t('order.quantitiesAndBook.totalValueLabel', 'TOTAL DOOR VALUE')
              : t('order.quantitiesAndBook.totalQtyLabel', 'TOTAL DOOR QUANTITY')
            : isValueSelected
            ? t('order.quantitiesAndBook.totalValueLabelWHL', 'TOTAL DRAFT VALUE')
            : t('order.quantitiesAndBook.totalQtyLabelWHL', 'TOTAL DRAFT QUANTITY')
        },
        cell2: {
          cellValue: quantity?.[`total${isValueSelected ? 'Value' : ''}BySizeQuantity`],
          cellCurrency,
          cellLabel: isValueSelected
            ? t('order.quantitiesAndBook.totalSizedValueLabel', 'TOTAL SIZED VALUE')
            : t('order.quantitiesAndBook.totalSizedQuantityLabel', 'TOTAL SIZED QUANTITY')
        }
      }
    }, [isValueSelected, priceData, isRetail, quantity, t])

    const sizesEle = useMemo(() => {
      // hide sizes by default
      return (
        <>
          {showProductSizes[rowData?.id] &&
            getSizes(rowData.id)?.map(({ id, name, isInactive = false }) => {
              return (
                <div className={classes.sizeContainer}>
                  <QtyCellValueText key={id} value={name} isInactive={isInactive} />
                </div>
              )
            })}
        </>
      )
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sizes, showProductSizes, rowData])

    const index = rowData?.id ? itemIndexObjectStore.getIndexByKey(rowData.id) : null
    const isBookingAllInProgress = isNumber(index)
      ? itemIndexObjectStore.isObjectDataLoading(
          index,
          PRODUCT_OPERATIONS_LOADERS.BOOKING_ALL_DOORS
        )
      : false

    const isProductActive: boolean = useProductStatus(rowData)
    if (!rowData) return <></>

    return (
      <div className={classes.bookQty} key={rowData.id}>
        {!summary?.doorOrder && (
          <div className={classes.bookUnBookCheckbox}>
            <QtyCellLabelText
              translation={t('orderTable.bookOrUnBookAll', 'BOOK / UN-BOOK ALL')}
            />
            <Checkbox
              onChange={(e, checked) => {
                bookUnbookDoors(
                  summary.clientOrder.clientOrderId,
                  rowData.id,
                  // pass only un excluded door order ids do be booked
                  getUnExcludedDoorIds(
                    rowData?.excludedInDoors,
                    filter(summary.doorOrders, doorOrder => !doorOrder?.deleted),
                    checked
                  ),
                  checked
                )
              }}
              disabled={isBookingAllInProgress || !isOrderEditable || !isProductActive}
              checked={areAllDoorsBooked}
              className={
                areAllDoorsBooked ? classes.bookQtyCheckbox : classes.bookQtyUnCheckbox
              }
              size='small'
              color='default'
              icon={<CheckBoxOutlineBlankIcon fontSize='small' />}
              checkedIcon={<CheckBoxIcon fontSize='small' />}
            />
          </div>
        )}
        <div style={{ marginTop: summary?.doorOrder ? 50 : 0 }}>
          {
            <div className={classes.totalActualQty}>
              {isNumber(bookedCells?.cell1?.cellValue) && (
                <>
                  <QtyCellLabelText translation={bookedCells.cell1.cellLabel} />
                  <div className={classes.totalValueContainer}>
                    <QtyCellValueText
                      value={`${bookedCells.cell1.cellCurrency}${formatNumberByLocale(
                        bookedCells.cell1.cellValue
                      )}`}
                    />
                  </div>
                </>
              )}
            </div>
          }
          {
            <div className={classes.totalSizedQty}>
              {sizes?.length &&
                isNumber(bookedCells?.cell2?.cellValue) &&
                (summary?.client?.captureSize ||
                  summary?.clientOrder?.client?.captureSize) && (
                  <>
                    <div className={classes.sizeLabel}>
                      <QtyCellLabelText translation={bookedCells.cell2.cellLabel} />
                    </div>
                    <QtyCellValueText
                      value={`${bookedCells.cell2.cellCurrency}${formatNumberByLocale(
                        bookedCells.cell2.cellValue
                      )}`}
                    />
                  </>
                )}
            </div>
          }
          {sizes?.length &&
          (summary?.client?.captureSize || summary?.clientOrder?.client?.captureSize) ? (
            <div className={classNames('flex justify-center', classes.sizeToggleButton)}>
              <Typography
                variant='toggle'
                color='blue'
                onClick={() => {
                  hideShowProductSizes(!showProductSizes[rowData?.id], rowData.id)
                }}
              >
                {showProductSizes[rowData?.id]
                  ? t('orderTable.hideProductSizes', 'Hide Product Sizes')
                  : t('orderTable.showProductSizes', 'Show Product Sizes')}
              </Typography>
            </div>
          ) : null}

          <div className={classes.sizesContainer}>{sizesEle}</div>
        </div>
      </div>
    )
  })

export default memo(BookQty)
